<template>
  <!-- Renders the contents of one <dd> in the detail facts list, directly under
       "Lampiran pendukung": the report is another document this activity hands
       the public, so it reads as one more row rather than a separate slab at
       the bottom of the page. The `data-lpj-*` hooks are unchanged. -->
  <span
    v-if="loading"
    class="gr-lpj__pending"
    data-lpj-public="true"
    aria-live="polite"
  >
    Memuat laporan...
  </span>

  <span v-else-if="errorMessage" class="gr-lpj__error" role="alert">
    {{ errorMessage }}
    <button type="button" class="gr-lpj__retry" @click="load">Coba lagi</button>
  </span>

  <span v-else-if="metadata?.exists" class="gr-lpj" data-lpj-public="true">
    <button
      type="button"
      class="gr-lpj__download"
      data-lpj-public-download="true"
      :disabled="downloading"
      @click="download"
    >
      <v-icon icon="mdi-file-download-outline" size="20" aria-hidden="true" />
      {{ downloading ? "Menyiapkan..." : "Unduh laporan" }}
    </button>
    <span
      v-if="metadata.uploadedAt"
      class="gr-lpj__meta"
      data-lpj-public-uploaded-at="true"
    >
      Diunggah {{ formatUploadDate(metadata.uploadedAt) }}
    </span>
  </span>

  <span v-else class="gr-lpj__pending" data-lpj-public="true">
    Menunggu laporan dari pembuat acara
  </span>
</template>

<script lang="ts" setup>
import { safeLpjError } from "~/features/lpj/lpj";

const props = defineProps<{ slug: string; eligible: boolean }>();

type Metadata = { exists: boolean; uploadedAt: string | null };
type FileUrl = { url: string; duration: number };

const metadata = ref<Metadata | null>(null);
const loading = ref(false);
const downloading = ref(false);
const errorMessage = ref("");

const statusCode = (error: unknown): number | undefined => {
  if (typeof error !== "object" || error === null) return undefined;
  const response = Reflect.get(error, "response");
  const value =
    typeof response === "object" && response !== null
      ? Reflect.get(response, "status")
      : undefined;
  return typeof value === "number" ? value : undefined;
};

const formatUploadDate = (value: string): string =>
  new Intl.DateTimeFormat("id-ID", { dateStyle: "long" }).format(
    new Date(value),
  );

const load = async (): Promise<void> => {
  if (!props.eligible) return;
  loading.value = true;
  errorMessage.value = "";
  try {
    const { $customFetch } = useNuxtApp();
    const response = await $customFetch<{ data: Metadata }>(
      `/public-activities/${encodeURIComponent(props.slug)}/lpj`,
    );
    metadata.value = response.data;
  } catch (error: unknown) {
    errorMessage.value = safeLpjError(statusCode(error));
  } finally {
    loading.value = false;
  }
};

const download = async (): Promise<void> => {
  downloading.value = true;
  errorMessage.value = "";
  try {
    const { $customFetch } = useNuxtApp();
    const response = await $customFetch<{ data: FileUrl }>(
      `/public-activities/${encodeURIComponent(props.slug)}/lpj/file-url`,
      { cache: "no-store" },
    );
    window.open(response.data.url, "_blank", "noopener,noreferrer");
  } catch (error: unknown) {
    errorMessage.value = safeLpjError(statusCode(error));
  } finally {
    downloading.value = false;
  }
};

onMounted(load);
</script>

<style scoped>
.gr-lpj {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 12px;
}

/* Same border, tint and green as the document attachments in the row above,
   laid out inline because a report is one file, not a gallery. */
.gr-lpj__download {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border: 1px solid var(--gr-line-strong);
  border-radius: 4px;
  background-color: var(--gr-green-tint);
  color: var(--gr-green-deep);
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.3;
  cursor: pointer;
}

@media (hover: hover) {
  .gr-lpj__download:hover {
    border-color: var(--gr-green-deep);
  }
}

.gr-lpj__download:disabled {
  cursor: progress;
  opacity: 0.7;
}

.gr-lpj__meta,
.gr-lpj__pending {
  color: var(--gr-ink-3);
  font-size: 0.875rem;
  line-height: 1.5;
}

.gr-lpj__error {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  color: var(--gr-danger);
  font-size: 0.875rem;
}

.gr-lpj__retry {
  color: var(--gr-danger);
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
}
</style>
